import { useState, useEffect, useCallback, useRef } from "react";
import { Menu } from "lucide-react";
import { useUser } from "@foundation/altzero_base/base/contextapi/UserContext";
import { supabase } from "@foundation/altzero_base/base/utils/supabaseClient";
import { theme } from "../../../theme";
import { ThreadSidebar } from "./ThreadSidebar";
import { ChatThread } from "./ChatThread";
import { CharlieRuntimeProvider } from "../runtime/CharlieRuntimeProvider";
import {
  listThreads,
  listArchivedThreads,
  createThread,
  updateThread,
  deleteThread,
  archiveThread,
  unarchiveThread,
  getThreadMessages,
  appendMessage,
  generateThreadTitle,
} from "../services/threadService";
import { convertToCreateParams } from "../runtime/charlieMessageAdapter";
import { sendMessageStream } from "../services/chatService";
import type { Message } from "../types/chat.types";

const FOLLOW_UP_QUESTION_POOL = [
  "What are the red flags that require calling 000 immediately?",
  "How do I perform a sideline concussion assessment?",
  "What is the graduated return-to-play protocol?",
  "What symptoms indicate a concussion?",
  "When should a player be removed from play?",
  "What does 'if in doubt, sit them out' mean?",
  "How long does concussion recovery typically take?",
  "Can a player return the same day after a concussion?",
  "What is CRT6 and how do I use it on the sideline?",
  "What should I do if a player loses consciousness?",
  "How do I explain concussion to a parent or guardian?",
  "What is post-concussion syndrome?",
  "How do I support an athlete returning to sport after concussion?",
  "What are the AIS guidelines for junior athletes?",
  "How do I log a concussion incident in the app?",
  "What is the difference between a concussion and a head knock?",
  "Are there specific rules for children with concussion?",
  "Can someone have a concussion without hitting their head?",
  "What questions should I ask a player to screen for concussion?",
  "What should coaches know about concussion in contact sports?",
];

type ThreadItem = {
  id: string;
  title?: string;
  status: "regular" | "archived";
};

type MessageItem = {
  id: string;
  role: "user" | "assistant";
  content: string;
  metadata?: Record<string, unknown>;
};

export const ChatLayout = () => {
  const { user } = useUser();
  const isAuthenticated = !!user?.id;

  const [showSources, setShowSources] = useState(true);
  const [orgId, setOrgId] = useState<string | undefined>(undefined);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Thread state (authenticated users only)
  const [threads, setThreads] = useState<ThreadItem[]>([]);
  const [archivedThreads, setArchivedThreads] = useState<ThreadItem[]>([]);
  const [currentThreadId, setCurrentThreadId] = useState<string | null>(null);
  const [messages, setMessages] = useState<MessageItem[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const usedQuestionIndicesRef = useRef(new Set<number>());
  const titleGeneratedRef = useRef(new Set<string>());

  // Pick 3 random non-repeating questions
  const pickFollowUpQuestions = useCallback((): string[] => {
    const pool = FOLLOW_UP_QUESTION_POOL;
    const used = usedQuestionIndicesRef.current;
    if (pool.length - used.size < 3) used.clear();
    const available = pool.reduce<number[]>((acc, _, i) => {
      if (!used.has(i)) acc.push(i);
      return acc;
    }, []);
    for (let i = available.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [available[i], available[j]] = [available[j], available[i]];
    }
    const picked = available.slice(0, 3);
    picked.forEach((i) => used.add(i));
    return picked.map((i) => pool[i]);
  }, []);

  const [initialSuggestions] = useState<string[]>(() => {
    const available = FOLLOW_UP_QUESTION_POOL.map((_, i) => i);
    for (let i = available.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [available[i], available[j]] = [available[j], available[i]];
    }
    return available.slice(0, 3).map((i) => FOLLOW_UP_QUESTION_POOL[i]);
  });

  // Resolve orgId
  useEffect(() => {
    if (!user?.id) return;
    const resolveOrgId = async () => {
      try {
        const metaOrgId = user.user_metadata?.org_id;
        if (metaOrgId) {
          setOrgId(metaOrgId as string);
          return;
        }
        const { data } = await supabase
          .from("base_organisation_members")
          .select("organisation_id")
          .eq("user_id", user.id)
          .eq("is_active", true)
          .limit(1)
          .maybeSingle();
        if (data?.organisation_id) {
          setOrgId(data.organisation_id as string);
          return;
        }
        const { data: profile } = await supabase
          .from("base_profiles")
          .select("tenant_id")
          .eq("id", user.id)
          .not("tenant_id", "is", null)
          .limit(1)
          .single();
        if (profile?.tenant_id) {
          setOrgId(profile.tenant_id as string);
        }
      } catch (err) {
        console.error("Failed to resolve org ID:", err);
      }
    };
    resolveOrgId();
  }, [user?.id]);

  // Load threads (authenticated only)
  const refreshThreadList = useCallback(async () => {
    if (!user?.id) return;
    try {
      const [active, archived] = await Promise.all([
        listThreads(user.id),
        listArchivedThreads(user.id),
      ]);
      setThreads(active.map((t) => ({ id: t.id, title: t.title, status: "regular" as const })));
      setArchivedThreads(archived.map((t) => ({ id: t.id, title: t.title, status: "archived" as const })));
    } catch (error) {
      console.error("Failed to refresh threads:", error);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }
    const load = async () => {
      await refreshThreadList();
      setIsLoading(false);
    };
    load();
  }, [isAuthenticated, refreshThreadList]);

  // Auto-select first thread
  useEffect(() => {
    if (isAuthenticated && threads.length > 0 && !currentThreadId) {
      setCurrentThreadId(threads[0].id);
    }
  }, [threads, currentThreadId, isAuthenticated]);

  // Load messages for current thread
  useEffect(() => {
    if (!currentThreadId) {
      setMessages([]);
      return;
    }
    const load = async () => {
      try {
        const msgs = await getThreadMessages(currentThreadId);
        setMessages(
          msgs.map((m) => ({
            id: m.id,
            role: m.role,
            content: m.content,
            metadata: {
              sources: m.sources,
              rawContent: m.raw_content,
              skillApplied: m.skill_applied,
              skillName: m.metadata?.skillName,
              followUpQuestions: m.metadata?.followUpQuestions,
              provider: m.provider,
              error: m.metadata?.error,
            },
          }))
        );
      } catch (error) {
        console.error("Failed to load messages:", error);
      }
    };
    load();
  }, [currentThreadId]);

  // Handle sending a message
  const handleSend = useCallback(
    async (content: string) => {
      if (!content.trim() || isRunning) return;

      let threadId = currentThreadId;

      // Auto-create thread for authenticated users
      if (isAuthenticated && !threadId) {
        try {
          const thread = await createThread({
            user_id: user!.id,
            org_id: orgId,
            provider: "knowledge-base",
            kb_id: null,
          });
          threadId = thread.id;
          setCurrentThreadId(threadId);
          await refreshThreadList();
        } catch (error) {
          console.error("Failed to create thread:", error);
          return;
        }
      }

      // Add user message
      const userMsgId = crypto.randomUUID();
      const userMsg: MessageItem = {
        id: userMsgId,
        role: "user",
        content,
      };
      setMessages((prev) => [...prev, userMsg]);

      // Persist user message (authenticated only)
      if (isAuthenticated && threadId) {
        try {
          const record = await appendMessage({
            thread_id: threadId,
            role: "user",
            content,
          });
          // Update with real ID
          setMessages((prev) =>
            prev.map((m) => (m.id === userMsgId ? { ...m, id: record.id } : m))
          );
        } catch (error) {
          console.error("Failed to persist user message:", error);
        }
      }

      // Send to AI via streaming
      setIsRunning(true);
      const streamingMsgId = crypto.randomUUID();
      // Add streaming placeholder immediately
      setMessages((prev) => [
        ...prev,
        { id: streamingMsgId, role: "assistant", content: "", metadata: { streaming: true } },
      ]);

      try {
        const history: Message[] = messages.map((m) => ({
          id: m.id,
          role: m.role,
          content: m.content,
          timestamp: new Date(),
        }));

        let accumulated = "";
        let streamedSources: string[] = [];
        let skillApplied = false;
        let skillAppliedName: string | null = null;

        await sendMessageStream(
          content,
          history,
          (token) => {
            accumulated += token;
            setMessages((prev) =>
              prev.map((m) =>
                m.id === streamingMsgId ? { ...m, content: accumulated } : m
              )
            );
          },
          (sources) => {
            streamedSources = sources;
          },
          (_meta) => {
            // done — finalized below
          },
          (error) => {
            console.error("Stream error:", error);
          },
          undefined,
          (skillData) => {
            // Replace streamed content with skill-enhanced version
            accumulated = skillData.content;
            skillApplied = skillData.skillApplied;
            skillAppliedName = skillData.skillName;
            setMessages((prev) =>
              prev.map((m) =>
                m.id === streamingMsgId ? { ...m, content: skillData.content } : m
              )
            );
          },
          orgId,
          "knowledge-base",
          user?.id
        );

        const followUps = pickFollowUpQuestions();
        const finalContent = accumulated || "I'm sorry, I couldn't generate a response. Please try again.";

        const assistantMsg: MessageItem = {
          id: streamingMsgId,
          role: "assistant",
          content: finalContent,
          metadata: {
            sources: streamedSources.length > 0 ? streamedSources : ["Knowledge Base"],
            followUpQuestions: followUps,
            provider: "knowledge-base",
            streaming: false,
            skillApplied,
            skillName: skillAppliedName,
          },
        };

        setMessages((prev) =>
          prev.map((m) => (m.id === streamingMsgId ? assistantMsg : m))
        );

        // Persist assistant message (authenticated only)
        if (isAuthenticated && threadId) {
          try {
            const record = await appendMessage({
              thread_id: threadId,
              role: "assistant",
              content: finalContent,
              sources: streamedSources.length > 0 ? streamedSources : null,
              provider: "knowledge-base",
              skill_applied: skillApplied,
              metadata: { followUpQuestions: followUps, skillName: skillAppliedName },
            });
            setMessages((prev) =>
              prev.map((m) => (m.id === streamingMsgId ? { ...m, id: record.id } : m))
            );
          } catch (error) {
            console.error("Failed to persist assistant message:", error);
          }

          // Auto-generate title after first exchange
          if (!titleGeneratedRef.current.has(threadId)) {
            titleGeneratedRef.current.add(threadId);
            generateThreadTitle([
              { role: "user", content },
              { role: "assistant", content: finalContent },
            ]).then(async (title) => {
              try {
                await updateThread(threadId!, { title });
                await refreshThreadList();
              } catch {
                // Non-critical
              }
            });
          }
        }
      } catch (error) {
        console.error("Chat error:", error);
        const errorContent = "I'm sorry, I'm having trouble connecting right now. Please try again in a moment.";
        setMessages((prev) =>
          prev.map((m) =>
            m.id === streamingMsgId
              ? { ...m, content: errorContent, metadata: { error: error instanceof Error ? error.message : "Unknown error", streaming: false } }
              : m
          )
        );

        if (isAuthenticated && threadId) {
          try {
            await appendMessage({
              thread_id: threadId,
              role: "assistant",
              content: errorContent,
              metadata: { error: error instanceof Error ? error.message : "Unknown error" },
            });
          } catch {
            // Double failure
          }
        }
      } finally {
        setIsRunning(false);
      }
    },
    [currentThreadId, messages, user, orgId, isAuthenticated, isRunning, pickFollowUpQuestions, refreshThreadList]
  );

  // Thread management handlers
  const handleNewThread = useCallback(async () => {
    if (!user?.id) return;
    try {
      const thread = await createThread({
        user_id: user.id,
        org_id: orgId,
        provider: "knowledge-base",
        kb_id: null,
      });
      setCurrentThreadId(thread.id);
      setMessages([]);
      await refreshThreadList();
      setSidebarOpen(false);
    } catch (error) {
      console.error("Failed to create thread:", error);
    }
  }, [user?.id, orgId, refreshThreadList]);

  const handleSelectThread = useCallback((threadId: string) => {
    setCurrentThreadId(threadId);
    setSidebarOpen(false);
  }, []);

  const handleRename = useCallback(
    async (threadId: string, newTitle: string) => {
      try {
        await updateThread(threadId, { title: newTitle });
        await refreshThreadList();
      } catch (error) {
        console.error("Failed to rename:", error);
      }
    },
    [refreshThreadList]
  );

  const handleArchive = useCallback(
    async (threadId: string) => {
      try {
        await archiveThread(threadId);
        if (currentThreadId === threadId) {
          setCurrentThreadId(null);
          setMessages([]);
        }
        await refreshThreadList();
      } catch (error) {
        console.error("Failed to archive:", error);
      }
    },
    [currentThreadId, refreshThreadList]
  );

  const handleUnarchive = useCallback(
    async (threadId: string) => {
      try {
        await unarchiveThread(threadId);
        await refreshThreadList();
      } catch (error) {
        console.error("Failed to unarchive:", error);
      }
    },
    [refreshThreadList]
  );

  const handleDelete = useCallback(
    async (threadId: string) => {
      try {
        await deleteThread(threadId);
        if (currentThreadId === threadId) {
          setCurrentThreadId(null);
          setMessages([]);
        }
        await refreshThreadList();
      } catch (error) {
        console.error("Failed to delete:", error);
      }
    },
    [currentThreadId, refreshThreadList]
  );

  const knowledgeReady = !!user?.id;

  return (
    <div className={theme.chat.page}>
      <div className="flex flex-1 overflow-hidden gap-3">
        {/* Thread sidebar (authenticated users only) */}
        {isAuthenticated && (
          <>
            {/* Mobile hamburger */}
            <button
              onClick={() => setSidebarOpen(true)}
              aria-label="Open conversations"
              className={`md:hidden fixed top-[72px] left-3 z-30 p-2 rounded-xl dark:bg-white/[0.06] bg-white border ${theme.border.base} shadow-sm`}
            >
              <Menu className={`w-5 h-5 ${theme.text.body}`} />
            </button>

            <ThreadSidebar
              threads={threads}
              archivedThreads={archivedThreads}
              activeThreadId={currentThreadId}
              isLoading={isLoading}
              onNewThread={handleNewThread}
              onSelectThread={handleSelectThread}
              onRename={handleRename}
              onArchive={handleArchive}
              onUnarchive={handleUnarchive}
              onDelete={handleDelete}
              isOpen={sidebarOpen}
              onClose={() => setSidebarOpen(false)}
            />
          </>
        )}

        {/* Main chat area */}
        <div className="flex-1 flex flex-col min-w-0">
          <ChatThread
            messages={messages}
            isRunning={isRunning}
            onSend={handleSend}
            showSources={showSources}
            onShowSourcesChange={setShowSources}
            initialSuggestions={initialSuggestions}
            isDisabled={!knowledgeReady}
            disabledMessage={!knowledgeReady ? "Sign in to use Knowledge Base chat" : undefined}
          />
        </div>
      </div>
    </div>
  );
};
